import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BasePageLayout,
  BaseBox,
  BaseTypography,
  BaseButton,
} from "../../Common/UI/exports";
import { ROLE_BASED_ACCESS } from "../../Utils/JWTTokenUtil";

function Home() {
  const [selectedProcess, setSelectedProcess] = useState("");
  const [selectedFlow, setSelectedFlow] = useState("");
  const navigate = useNavigate();
  const email = localStorage.getItem("email");

  const handleNext = (processId, flow) => {
    if (processId === "release-management") {
      if (flow === "gitflow")
        navigate("/gitflow-release-management", { state: { email } });
      else if (flow === "trunk-based-development")
        navigate("/tbd-release-management", { state: { email } });
    } else if (processId === "iac-tfstate-unlock") {
      if (flow === "terraform")
        navigate("/iac-tfstate-unlock", { state: { email } });
    } else if (processId === "multi-deployment") {
      navigate("/multi-deployment", { state: { email } });
    } else if (processId === "sctk") {
      navigate("/sctk", { state: { email } });
    } else if (processId === "aws-management") {
      if (flow === "aws-secrets-manager")
        navigate("/aws-secrets-manager", { state: { email } });
      else if (flow === "aws-parameter-store-manager")
        navigate("/aws-parameter-store-manager", { state: { email } });
      else if (flow === "aws-cache-manager")
        navigate("/aws-cache-manager", { state: { email } });
    } else if (processId === "utils") {
      navigate("/util", { state: { email } });
    } else if (processId === "role-management") {
      navigate("/role-management", { state: { email } });
    } else if (processId === "user-management") {
      navigate("/user-management", { state: { email } });
    }
  };

  const processes = [
    ROLE_BASED_ACCESS.RELEASE_MANAGEMENT() && {
      id: "release-management",
      title: "Release Management",
      desc: "Automate GitFlow or Trunk-Based release & hotfix pipelines.",
      icon: "🚀",
      hasFlow: true,
    },
    ROLE_BASED_ACCESS.IAC_TFSTATE_UNLOCK() && {
      id: "iac-tfstate-unlock",
      title: "IaC / TF State Unlock",
      desc: "Unlock Terraform remote state locks",
      icon: "🛠️",
      hasFlow: true,
    },
    ROLE_BASED_ACCESS.MULTI_DEPLOYMENT() && {
      id: "multi-deployment",
      title: "Multi-Deployment",
      desc: "Deploy to multiple environments with one click.",
      icon: "🧩",
      hasFlow: false,
    },
    ROLE_BASED_ACCESS.SCTK() && {
      id: "sctk",
      title: "SCTK",
      desc: "Security and compliance automation toolkit.",
      icon: "🔒",
      hasFlow: false,
    },
    ROLE_BASED_ACCESS.AWS_MANAGEMENT() && {
      id: "aws-management",
      title: "AWS Management",
      desc: "AWS Management Flows.",
      icon: "🚀",
      hasFlow: true,
    },
    ROLE_BASED_ACCESS.UTILS() && {
      id: "utils",
      title: "Utils",
      desc: "Common Utilities",
      icon: "🛠️",
      hasFlow: false,
    },
    ROLE_BASED_ACCESS.USER_MANAGEMENT() && {
      id: "user-management",
      title: "User Management",
      desc: "Managing Users across the system",
      icon: "👥",
      hasFlow: false,
    },
    ROLE_BASED_ACCESS.ROLE_MANAGEMENT() && {
      id: "role-management",
      title: "Role Management",
      desc: "Control roles across the system",
      icon: "🛡️",
      hasFlow: false,
    },
  ].filter(Boolean);

  const handleCardClick = (proc) => {
    console.log("txt");
    setSelectedProcess(proc.id);
    setSelectedFlow("");

    // Navigate immediately if no flow selection is required
    if (!proc.hasFlow) {
      handleNext(proc.id);
    }
  };

  return (
    <BasePageLayout sidebarItems={[]}>
      <BaseBox
        sx={{
          textAlign: "center",
          margin: "0",
          maxWidth: "900px",
          padding: 2,
        }}
      >
        {/* Process Cards */}
        <BaseBox
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 3,
            justifyContent: "center",
          }}
        >
          {processes.map((proc) => (
            <BaseBox
              key={proc.id}
              onClick={() => handleCardClick(proc)}
              sx={{
                p: 3,
                borderRadius: "16px",
                backgroundColor:
                  selectedProcess === proc.id ? "#e0f7fa" : "background.paper",
                boxShadow:
                  selectedProcess === proc.id
                    ? "0 6px 14px rgba(0,0,0,0.25)"
                    : "0 3px 8px rgba(0,0,0,0.12)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                textAlign: "center",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                },
              }}
            >
              <BaseTypography sx={{ fontSize: "2rem" }}>
                {proc.icon}
              </BaseTypography>
              <BaseTypography sx={{ fontWeight: 600, mt: 1 }}>
                {proc.title}
              </BaseTypography>
              <BaseTypography
                sx={{
                  color: "text.secondary",
                  fontSize: "0.9rem",
                  mt: 0.5,
                }}
              >
                {proc.desc}
              </BaseTypography>
            </BaseBox>
          ))}
        </BaseBox>

        {/* Flow Selection (only for release-management) */}
        {selectedProcess === "release-management" && (
          <BaseBox sx={{ mt: 4 }}>
            <BaseTypography sx={{ mb: 2, fontWeight: 500 }}>
              Select a flow:
            </BaseTypography>
            <BaseBox
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <BaseButton
                onClick={() => {
                  setSelectedFlow("gitflow");
                  handleNext("release-management", "gitflow");
                }}
                sx={{ px: 3, py: 1.2 }}
              >
                GitFlow
              </BaseButton>
              <BaseButton
                onClick={() => {
                  setSelectedFlow("trunk-based-development");
                  handleNext("release-management", "trunk-based-development");
                }}
                sx={{ px: 3, py: 1.2 }}
                disabled={true}
              >
                Trunk-Based Development
              </BaseButton>
            </BaseBox>
          </BaseBox>
        )}

        {/* Flow Selection (only for aws-management) */}
        {selectedProcess === "aws-management" && (
          <BaseBox sx={{ mt: 4 }}>
            <BaseTypography sx={{ mb: 2, fontWeight: 500 }}>
              Select a flow:
            </BaseTypography>
            <BaseBox
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {ROLE_BASED_ACCESS.AWS_SECRETS_MANAGER?.() && (
                <BaseButton
                  onClick={() => {
                    setSelectedFlow("aws-secrets-manager");
                    handleNext("aws-management", "aws-secrets-manager");
                  }}
                  sx={{ px: 3, py: 1.2 }}
                >
                  AWS Secrets Manager
                </BaseButton>
              )}

              {ROLE_BASED_ACCESS.AWS_PARAMETER_STORE_MANAGER?.() && (
                <BaseButton
                  onClick={() => {
                    setSelectedFlow("aws-parameter-store-manager");
                    handleNext("aws-management", "aws-parameter-store-manager");
                  }}
                  sx={{ px: 3, py: 1.2 }}
                >
                  AWS Parameter Store Manager
                </BaseButton>
              )}

              {ROLE_BASED_ACCESS.AWS_CACHE_MANAGER?.() && (
                <BaseButton
                  onClick={() => {
                    setSelectedFlow("aws-cache-manager");
                    handleNext("aws-management", "aws-cache-manager");
                  }}
                  sx={{ px: 3, py: 1.2 }}
                >
                  AWS Cache Manager
                </BaseButton>
              )}
            </BaseBox>
          </BaseBox>
        )}

        {/* Flow Selection (only for IaC / TF State Unlock) */}
        {selectedProcess === "iac-tfstate-unlock" && (
          <BaseBox sx={{ mt: 4 }}>
            <BaseTypography sx={{ mb: 2, fontWeight: 500 }}>
              Select a flow:
            </BaseTypography>
            <BaseBox
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <BaseButton
                onClick={() => {
                  setSelectedFlow("terraform");
                  handleNext("iac-tfstate-unlock", "terraform");
                }}
                sx={{ px: 3, py: 1.2 }}
              >
                Terraform
              </BaseButton>
            </BaseBox>
          </BaseBox>
        )}
      </BaseBox>
    </BasePageLayout>
  );
}

export default Home;
